import type { UserMentionOutput } from "@orsane/schema/mentions";
import type { UserOutput } from "@orsane/schema/users";
import React, { useEffect, useImperativeHandle, useState } from "react";
import { UserAvatar } from "../UserAvatar";

interface MentionListProps {
    items: UserOutput[];
    command: (props: UserMentionOutput) => void;
}

interface MentionListRef {
    onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const MentionList = React.forwardRef<MentionListRef, MentionListProps>((props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const selectItem = (index: number) => {
        const item = props.items[index];

        if (item) {
            // Generate a unique idempotency key for this mention
            const idempotencyKey = crypto.randomUUID();

            props.command({ id: item.id, label: item.name, email: item.email, idempotencyKey });
        }
    };

    const upHandler = () => {
        setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length);
    };

    const downHandler = () => {
        setSelectedIndex((selectedIndex + 1) % props.items.length);
    };

    const enterHandler = () => {
        selectItem(selectedIndex);
    };

    useEffect(() => setSelectedIndex(0), []);

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }) => {
            if (event.key === "ArrowUp") {
                upHandler();
                return true;
            }

            if (event.key === "ArrowDown") {
                downHandler();
                return true;
            }

            if (event.key === "Enter") {
                enterHandler();
                return true;
            }

            return false;
        },
    }));

    return (
        <div className="rounded-md border border-neutral-200 bg-white p-1 text-orsane-primary shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 dark:border-neutral-800 dark:bg-neutral-950 dark:text-zinc-50 flex flex-col gap-0.5">
            {props.items.length ? (
                props.items.map((item, index) => (
                    <button
                        type="button"
                        className={`relative flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5  outline-none data-[disabled=true]:pointer-events-none data-[selected='true']:bg-neutral-100 hover:bg-neutral-100 data-[selected=true]:text-zinc-900 data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 dark:data-[selected='true']:bg-neutral-800 dark:data-[selected=true]:text-zinc-50 ${
                            index === selectedIndex ? "bg-neutral-100 hover:bg-neutral-100" : ""
                        }`}
                        key={item.id}
                        onClick={() => selectItem(index)}
                    >
                        <UserAvatar user={item} size="xs" />
                        {item.name}
                    </button>
                ))
            ) : (
                <div className="px-2 py-1">No result</div>
            )}
        </div>
    );
});

MentionList.displayName = "MentionList";

export default MentionList;
